'use client';

import { ChevronLeft } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Spinner } from './loading/Spinner';
import { cn } from '../lib/cn';
import { resolveApiErrorToastMessage } from '../lib/api/localizeApiError';
import {
  createProjectRole,
  setProjectRoleArchived,
  updateProjectRole,
  type ProjectPermission,
  type ProjectPermissionCatalogItem,
  type ProjectRole,
} from '../lib/api/projectPermissions';
import {
  buildFeatureLadders,
  featureLevelIndex,
  summaryFeatureLevels,
  withFeatureLevel,
  type FeatureLadder,
} from '../lib/projectPermissionLevels';
import { useProjectPermissionLabels } from '../hooks/useProjectPermissionLabels';

type ProjectRolesSectionProps = {
  projectId: string;
  token: string;
  roles: ProjectRole[];
  catalog: ProjectPermissionCatalogItem[];
  isLoading: boolean;
  canManageRoles: boolean;
  onChanged: () => void | Promise<void>;
};

type RoleDraft = {
  id: string | null;
  name: string;
  description: string;
  permissions: Set<ProjectPermission>;
};

const emptyDraft = (): RoleDraft => ({ id: null, name: '', description: '', permissions: new Set() });

/** 요약에 노출할 기능 수. 넘치는 만큼은 "+N"으로 접는다. */
const SUMMARY_LIMIT = 3;

/**
 * 프로젝트 역할 관리 UI (`roles.manage` 전용).
 *
 * ## 왜 모달이 아니라 push view인가
 *
 * 이 화면은 이미 워크스페이스 모달 > 프로젝트 설정 모달 안에 있다. 역할 편집을 모달로 띄우면
 * dialog가 3~4겹이 되어 ESC가 어디까지 닫는지 예측할 수 없고, daisyUI `.modal-box`가 fixed
 * 자손의 컨테이닝 블록이 되는 문제(BaseModal의 portal 주석 참조)를 겹칠 때마다 다시 만난다.
 * 목록 ↔ 편집을 같은 패널에서 밀어 넣으면 깊이가 늘지 않는다.
 *
 * ## permission 편집
 *
 * 목록은 화면 배열이 아니라 **서버 카탈로그**를 그린다. 화면에 하드코딩하면 서버가 permission을
 * 추가해도 UI에서 고를 수 없어 두 축이 조용히 어긋난다.
 *
 * 편집은 permission 키 하나하나가 아니라 "기능 × 접근 수준"으로 받는다. 키를 그대로 나열하면
 * 40개가 넘는 체크박스가 되고, `write`만 있고 `read`는 없는 무의미한 조합까지 표현 가능해진다.
 *
 * built-in 역할은 코드가 SSOT라 편집·보관을 제공하지 않는다(서버도 거부한다).
 */
export function ProjectRolesSection({
  projectId,
  token,
  roles,
  catalog,
  isLoading,
  canManageRoles,
  onChanged,
}: ProjectRolesSectionProps) {
  const t = useTranslations('projects.roles');
  const tCommon = useTranslations('common');
  const tRoot = useTranslations();
  const { roleName, featureLabel, actionLabel } = useProjectPermissionLabels();

  const [draft, setDraft] = useState<RoleDraft | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [archivingRoleId, setArchivingRoleId] = useState<string | null>(null);

  const ladders = useMemo(() => buildFeatureLadders(catalog), [catalog]);

  const groups = useMemo(() => {
    const active = roles.filter((role) => role.archivedAt === null);
    return {
      builtIn: active.filter((role) => role.isBuiltIn),
      custom: active.filter((role) => !role.isBuiltIn),
      archived: roles.filter((role) => role.archivedAt !== null),
    };
  }, [roles]);

  const openCreate = () => setDraft(emptyDraft());
  const openEdit = (role: ProjectRole) =>
    setDraft({
      id: role.id,
      name: role.name,
      description: role.description ?? '',
      permissions: new Set(role.permissions),
    });

  const setFeatureLevel = (ladder: FeatureLadder, levelIndex: number) => {
    setDraft((prev) =>
      prev ? { ...prev, permissions: withFeatureLevel(prev.permissions, ladder, levelIndex) } : prev,
    );
  };

  const handleSave = async () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error(t('nameRequired'));
      return;
    }

    setIsSaving(true);
    try {
      const payload = {
        name: draft.name.trim(),
        description: draft.description.trim() || null,
        permissions: [...draft.permissions],
      };
      if (draft.id) {
        await updateProjectRole(token, projectId, draft.id, payload);
      } else {
        await createProjectRole(token, projectId, payload);
      }
      // 저장에 성공했을 때만 목록으로 돌아간다. 실패 시 돌아가면 입력이 사라진다.
      setDraft(null);
      await onChanged();
    } catch (error) {
      const message = resolveApiErrorToastMessage(tRoot, error);
      if (message) toast.error(message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleArchive = async (role: ProjectRole) => {
    setArchivingRoleId(role.id);
    try {
      await setProjectRoleArchived(token, projectId, role.id, role.archivedAt === null);
      await onChanged();
    } catch (error) {
      const message = resolveApiErrorToastMessage(tRoot, error);
      if (message) toast.error(message);
    } finally {
      setArchivingRoleId(null);
    }
  };

  const renderSummary = (role: ProjectRole) => {
    const levels = summaryFeatureLevels(ladders, role.permissions);
    if (levels.length === 0) return <span className="text-[var(--color-fg-muted)]">{t('summaryNone')}</span>;
    const shown = levels.slice(0, SUMMARY_LIMIT);
    const rest = levels.length - shown.length;
    return (
      <>
        {shown.map((level) => (
          <span
            key={level.feature}
            className="rounded-full px-2 py-0.5"
            style={{ background: 'var(--chip-neutral-bg)', color: 'var(--chip-neutral-fg)' }}
          >
            {featureLabel(level.feature)} {actionLabel(level.action)}
          </span>
        ))}
        {rest > 0 ? <span className="text-[var(--color-fg-subtle)]">{t('summaryMore', { count: rest })}</span> : null}
      </>
    );
  };

  const renderRole = (role: ProjectRole) => (
    <li
      key={role.id}
      className="flex min-w-0 items-start justify-between gap-3 rounded-xl border border-base-300 bg-base-100 p-3"
    >
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <span className="min-w-0 truncate text-sm font-medium">{roleName(role)}</span>
          {role.archivedAt ? <span className="badge badge-warning badge-xs shrink-0">{t('archived')}</span> : null}
        </div>
        {role.description ? (
          <p className="mt-0.5 truncate text-xs text-[var(--color-fg-muted)]">{role.description}</p>
        ) : null}
        <div className="mt-1.5 flex flex-wrap items-center gap-1 text-[11px]">{renderSummary(role)}</div>
        <p className="mt-1 text-xs text-[var(--color-fg-subtle)]">
          {t('memberCount', { count: role.assignedMemberCount })}
        </p>
      </div>
      {canManageRoles && !role.isBuiltIn ? (
        <div className="flex shrink-0 items-center gap-2">
          <button type="button" className="btn btn-ghost btn-xs" onClick={() => openEdit(role)}>
            {tCommon('actions.edit')}
          </button>
          <button
            type="button"
            className="btn btn-ghost btn-xs"
            disabled={archivingRoleId === role.id}
            onClick={() => void handleArchive(role)}
          >
            {role.archivedAt ? t('unarchive') : t('archive')}
          </button>
        </div>
      ) : null}
    </li>
  );

  const renderGroup = (title: string, list: ProjectRole[]) =>
    list.length === 0 ? null : (
      <div className="mt-4 first:mt-0">
        <h4 className="mb-2 text-xs font-medium text-[var(--color-fg-subtle)]">{title}</h4>
        <ul className="space-y-2">{list.map(renderRole)}</ul>
      </div>
    );

  if (draft) {
    return (
      <section aria-labelledby="project-role-editor-heading">
        <div className="mb-3 flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm gap-1 px-2"
            disabled={isSaving}
            onClick={() => setDraft(null)}
          >
            <ChevronLeft className="h-4 w-4" aria-hidden />
            {t('backToList')}
          </button>
        </div>

        <h3 id="project-role-editor-heading" className="mb-4 text-sm font-semibold text-base-content">
          {draft.id ? t('editRole') : t('createRole')}
        </h3>

        <div className="space-y-4">
          <div className="form-control">
            <label className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]" htmlFor="project-role-name">
              {t('name')}
            </label>
            <input
              id="project-role-name"
              className="input input-bordered input-sm w-full"
              value={draft.name}
              onChange={(event) => setDraft({ ...draft, name: event.target.value })}
            />
          </div>
          <div className="form-control">
            <label
              className="mb-1.5 text-xs font-medium text-[var(--color-fg-subtle)]"
              htmlFor="project-role-description"
            >
              {t('roleDescription')}
            </label>
            <input
              id="project-role-description"
              className="input input-bordered input-sm w-full"
              value={draft.description}
              onChange={(event) => setDraft({ ...draft, description: event.target.value })}
            />
          </div>

          <div>
            <p className="mb-2 text-xs font-medium text-[var(--color-fg-subtle)]">{t('permissions')}</p>
            <div className="space-y-1 rounded-xl border border-base-300 p-2">
              {ladders.map((ladder) => {
                const selected = featureLevelIndex(ladder, draft.permissions);
                const feature = featureLabel(ladder.feature);
                return (
                  <div
                    key={ladder.feature}
                    className="flex min-w-0 items-center justify-between gap-3 rounded-lg px-2 py-1.5 hover:bg-base-200"
                  >
                    <span className="min-w-0 flex-1 truncate text-sm">{feature}</span>
                    <div className="join shrink-0" role="radiogroup" aria-label={feature}>
                      {[-1, ...ladder.actions.map((_, index) => index)].map((levelIndex) => {
                        const action = levelIndex < 0 ? 'none' : ladder.actions[levelIndex];
                        const label = actionLabel(action);
                        return (
                          <label
                            key={action}
                            className={cn(
                              'btn join-item btn-xs',
                              selected === levelIndex ? 'btn-primary' : 'btn-ghost border border-base-300',
                            )}
                          >
                            <input
                              type="radio"
                              className="sr-only"
                              name={`project-role-permission-${ladder.feature}`}
                              aria-label={`${feature} ${label}`}
                              checked={selected === levelIndex}
                              onChange={() => setFeatureLevel(ladder, levelIndex)}
                            />
                            {label}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="mt-2 text-xs text-[var(--color-fg-muted)]">{t('enterAlwaysIncluded')}</p>
          </div>
        </div>

        {/*
         * 권한 21행을 지나야 저장 버튼에 닿는다. 부모 패널이 스크롤 주체이므로 sticky로 붙여
         * 스크롤 위치와 무관하게 저장·취소가 손에 닿게 한다.
         */}
        <div className="sticky bottom-0 mt-4 flex justify-end gap-2 border-t border-base-200 bg-base-100 py-3">
          <button type="button" className="btn btn-ghost btn-sm" disabled={isSaving} onClick={() => setDraft(null)}>
            {tCommon('actions.cancel')}
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            disabled={isSaving}
            onClick={() => void handleSave()}
          >
            {isSaving ? tCommon('loading') : tCommon('actions.save')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section aria-labelledby="project-roles-heading">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h3 id="project-roles-heading" className="text-sm font-semibold text-base-content">
            {t('title')}
          </h3>
          <p className="text-xs text-[var(--color-fg-muted)]">{t('description')}</p>
        </div>
        {canManageRoles ? (
          <button type="button" className="btn btn-outline btn-sm shrink-0" onClick={openCreate}>
            {t('createRole')}
          </button>
        ) : null}
      </div>

      {isLoading ? (
        <Spinner />
      ) : (
        <>
          {renderGroup(t('builtInRolesTitle'), groups.builtIn)}
          {groups.custom.length > 0 ? (
            renderGroup(t('customRolesTitle'), groups.custom)
          ) : (
            <div className="mt-4 rounded-xl border border-dashed border-base-300 p-6 text-center">
              <h4 className="mb-1 text-sm font-semibold text-base-content">{t('emptyCustomTitle')}</h4>
              <p className="text-xs text-[var(--color-fg-muted)]">{t('emptyCustomDescription')}</p>
            </div>
          )}
          {renderGroup(t('archivedRolesTitle'), groups.archived)}
        </>
      )}
    </section>
  );
}
